import React, { useState, ChangeEvent, useRef } from 'react'

import Button from '../../../scentregroup-ui/components/form/button'
import Form from '../../../scentregroup-ui/components/form/form'
import Paragraph from '../../../scentregroup-ui/components/paragraph'
import Textfield from '../../../scentregroup-ui/components/form/textfield'
import Row from '../../../scentregroup-ui/components/form/row'

import {
  Result,
  createErrorResult,
  createSuccessResult,
} from '../../../lib/types/result'
import {
  CreateNewPasswordError,
  CreateNewPassword,
} from '../../../lib/apiService'
import { createFormValidator } from '../../../lib/formValidation'
import OnboardingForm from '../onboarding-form'
import { isEmptyString, isEmail } from '../../../lib/validators'
import useAnalytics from '../../../lib/analytics/use-analytics'
import { logError, trackPSteps } from '../../../lib/analytics/events'
import style from './index.scss'

const EMAIL_IS_EMPTY = 'Please enter a valid email address and try again'
const getErrorMessages = () => {
  return {
    EMAIL_IS_EMPTY,
    EMAIL_IS_INVALID: EMAIL_IS_EMPTY,
    EMAIL_IS_UNUSED:
      'There isn’t an active Westfield account for this email. Please sign up instead or try another one.',
    EMAIL_HAS_PASSWORD:
      'There is an active Westfield account for this email. Please login instead or try another one.',
    EMAIL_IS_REJECTED: 'The email is not valid. Please try another one.',
  }
}

const validateEmail = (email: string): Result<string> => {
  const errors = getErrorMessages()

  if (isEmptyString(email)) {
    return createErrorResult(errors.EMAIL_IS_EMPTY)
  }

  return isEmail(email)
    ? createSuccessResult()
    : createErrorResult(errors.EMAIL_IS_INVALID)
}

export interface Values extends Record<string, unknown> {
  email: string
}

export interface CreateNewPasswordValues extends Record<string, unknown> {
  email: string
}

interface Errors {
  email: string
}

export interface OnChangeEmail {
  (email: string): void
}

type OnCreatePasswordSuccessData = CreateNewPasswordValues

export interface OnCreateNewPasswordSuccess {
  (data: OnCreatePasswordSuccessData): void
}

interface OnCreatePasswordFailureData extends CreateNewPasswordValues {
  errors: CreateNewPasswordError[]
}

export interface OnCreatePasswordFailure {
  (data: OnCreatePasswordFailureData): void
}

interface Props {
  draftEmail: string
  onChangeEmail: OnChangeEmail
  onCreateNewPasswordSuccess: OnCreateNewPasswordSuccess
  onCreateNewPasswordFailure: OnCreatePasswordFailure
  createNewPassword: CreateNewPassword
}

const CreateNewPasswordForm: React.FC<Props> = (props) => {
  const [values, setValues] = useState<Values>({
    email: props.draftEmail,
  })

  const [errors, setErrors] = useState<Errors>({
    email: '',
  })

  const setEmailError = (error: string) =>
    setErrors((state) => ({ ...state, email: error }))

  const [isCheckingCreateNewPassword, setIsCheckingCreateNewPassword] =
    useState(false)

  const [isBlurValidationEnabled, setIsBlurValidationEnabled] = useState(true)

  const emailInput = useRef<HTMLInputElement>(null)

  const { dataLayer } = useAnalytics()

  const validateCreateNewPasswordForm = createFormValidator<Values>(
    { email: values.email },
    (fieldName, errorValue) => {
      setEmailError(errorValue)
      if (errorValue) {
        dataLayer(
          logError('Create Password Onboarding Validation Error', errorValue)
        )
      }
    },
    () => [
      {
        validationFn: validateEmail,
        fieldName: 'email',
        fieldElement: emailInput.current,
      },
    ]
  )

  const onBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Without this guard, when a field is on focus and the submit button is
    // clicked, the onBlur validation may cause the height of the form to
    // change, making it so that the click lands outside the submit button!
    // This is an issue in DGC forms at the time of this writing.
    if (!isBlurValidationEnabled) {
      return
    }

    validateCreateNewPasswordForm(e.target.name)
  }

  const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    // we need to cache these due to https://reactjs.org/docs/events.html#event-pooling
    const { value } = e.target

    setValues((state) => ({ ...state, email: value }))
    props.onChangeEmail(value)
  }

  const onSubmit = async (e: ChangeEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const validationResult = validateCreateNewPasswordForm()

    if (!validationResult.success) {
      return
    }

    setIsCheckingCreateNewPassword(true)
    const result = await props.createNewPassword(values.email)
    setIsCheckingCreateNewPassword(false)

    if (result.success) {
      dataLayer(trackPSteps(1))
      return props.onCreateNewPasswordSuccess({
        email: values.email,
      })
    }

    const formErrors: Partial<Errors> = {}
    const systemErrors: CreateNewPasswordError[] = []
    const errors = getErrorMessages()

    for (const resultError of result.error) {
      switch (resultError) {
        case CreateNewPasswordError.Invalid:
          formErrors.email = errors.EMAIL_IS_INVALID
          break
        case CreateNewPasswordError.UnusedEmail:
          formErrors.email = errors.EMAIL_IS_UNUSED
          break
        case CreateNewPasswordError.EmailHasPassword:
          formErrors.email = errors.EMAIL_HAS_PASSWORD
          break
        case CreateNewPasswordError.Rejected:
          formErrors.email = errors.EMAIL_IS_REJECTED
          break
        default:
          systemErrors.push(resultError)
      }
    }

    if (Object.keys(formErrors).length > 0) {
      setErrors((state) => ({ ...state, ...formErrors }))
      dataLayer(logError('Create Password Error', formErrors.toString()))
    }

    if (systemErrors.length > 0) {
      props.onCreateNewPasswordFailure({
        email: values.email,
        errors: systemErrors,
      })
      dataLayer(logError('Create Password Error', systemErrors.toString()))
    }
  }

  const disableBlurValidation = () => setIsBlurValidationEnabled(false)
  const enableBlurValidation = () => setIsBlurValidationEnabled(true)

  return (
    <OnboardingForm
      title="Create a password"
      form={
        <Form noValidate onSubmit={onSubmit} className={style.form}>
          <Row>
            <Textfield
              innerRef={emailInput}
              type="email"
              label="Email address"
              name="email"
              id="email"
              value={values.email}
              inputMode="email"
              autoComplete="email"
              errorMessage={errors.email}
              isHorizontal={false}
              onChange={onChangeEmail}
              onBlur={onBlur}
            />
          </Row>

          <Row wrapperClassName={style.descriptionRow}>
            <Paragraph>
              We will email you a link to create a password.
            </Paragraph>
          </Row>

          <Row>
            <div
              data-testid="submit-button-wrapper"
              onMouseEnter={disableBlurValidation}
              onMouseLeave={enableBlurValidation}
            >
              <Row>
                <Button
                  buttonType="submit"
                  loading={isCheckingCreateNewPassword}
                >
                  Continue
                </Button>
              </Row>
            </div>
          </Row>
        </Form>
      }
    />
  )
}

export default CreateNewPasswordForm
